import { Injectable, Injector } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { LexbeCase } from '../models/case.model';
import { CaseResourceToLexbeCaseMapper } from '../models/mappers/case-resource-to-lexbe-case.mapper';
import { LexbeCaseToCaseResourceMapper } from '../models/mappers/lexbe-case-to-case-resource.mapper';
import { CaseResource } from '../models/resource/case.resource';
import { SdkBase } from '../utilities/sdk-base/sdk.base';

@Injectable({ providedIn: 'root' })
export class CaseSdk extends SdkBase {

	constructor(injector: Injector) {
		super(injector);
	}

	public getCases(orderBy?: [string, "ASC" | "DESC"], term?: string): Observable<LexbeCase[]> {
		let query = '';
		let sort = '';
		if (term && term?.length > 0)
			query = `searchTerm=${term}`
		if (orderBy && orderBy[0]?.length > 0)
			sort = `${query.length > 0 ? '&' : ''}sortBy=${orderBy[0]}&sortOrder=${orderBy[1]}`
		return this.makeHttpRequest<CaseResource[]>('get', `api2/cases?${query}${sort}`)
			.pipe(map((response) => {
			const caseMapper = new CaseResourceToLexbeCaseMapper();
			return caseMapper.mapCollection(response);
		}));
	}

	public getCasesByAccountId(accountId: string): Observable<LexbeCase[]> {
		return this.makeHttpRequest<CaseResource[]>('get', `api2/cases/${accountId}`).pipe(map((response) => {
			const caseMapper = new CaseResourceToLexbeCaseMapper();
			return caseMapper.mapCollection(response);
		}));
	}

	public getCaseUsers(caseId: string): Observable<LexbeCase> {
		return this.makeHttpRequest<CaseResource>('get', `api2/cases/${caseId}/users`).pipe(map((response) => {
			const caseMapper = new CaseResourceToLexbeCaseMapper();
			return caseMapper.map(response);
		}));
	}

	public getCaseDetails(caseId: string): Observable<LexbeCase> {
		return this.makeHttpRequest<CaseResource>('get', `api2/cases/${caseId}/details`).pipe(map((response) => {
			const caseMapper = new CaseResourceToLexbeCaseMapper();
			return caseMapper.map(response);
		}));
	}

	public getUserCasesUploadedDoc(userId: string, accountId: string): Observable<string[]> {
		return this.makeHttpRequest<string[]>('get', `api2/cases/${accountId}/docs/${userId}`).pipe(
			map((response) => response),
			catchError(() => {
				return of([]);
			}));
	}

	public put(lexbeCase: LexbeCase): Observable<LexbeCase> {
		const mapper = new LexbeCaseToCaseResourceMapper();
		return this.makeHttpRequest<CaseResource>('put', `api2/cases/${lexbeCase.caseId}`, mapper.map(lexbeCase))
			.pipe(map((response) => {
				const caseMapper = new CaseResourceToLexbeCaseMapper();
				return caseMapper.map(response);
			}));
	}

	public patchCase(caseId: string, deltaCase: Partial<CaseResource>): Observable<void> {
		const body = [];
		for (const key in deltaCase) {
			body.push({
				op: "replace",
				path: `/${key}`,
				value: deltaCase[key]
			})
		}
		return this.makeHttpRequest<void>('patch', `api2/cases/${caseId}`, body);
	}

	public deleteCase(caseId: string): Observable<void> {
		return this.makeHttpRequest<void>('delete', `api2/cases/${caseId}`);
	}
}
